import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import { useTravlData } from './TravlContext';

const TIME_BANDS = [
    '0000-0100', '0100-0200', '0200-0300', '0300-0400', '0400-0500', '0500-0600',
    '0600-0700', '0700-0800', '0800-0900', '0900-1000', '1000-1100', '1100-1200',
    '1200-1300', '1300-1400', '1400-1500', '1500-1600', '1600-1700', '1700-1800',
    '1800-1900', '1900-2000', '2000-2100', '2100-2200', '2200-2300', '2300-0000'
];

const modeCodeMap: Record<string, string> = {
    BUS: "Bus",
    CAR: "Car",
    CARA: "All Car Drivers",
    CARD: "Car Driver (alone)",
    CARM: "Car Driver (with pass)",
    CARP: "Car Passenger",
    COAC: "Coach",
    DLR: "DLR",
    HGV: "HGV",
    MOTC: "Motor Cycle",
    OTHR: "Other",
    PARB: "Park and Ride Bus",
    PEDC: "Pedal Cycle",
    RAIL: "Rail",
    RBOT: "River Boat",
    TAXI: "Taxi",
    TAXO: "Taxi Occupants",
    TRAM: "Tram",
    TUBE: "Underground",
    UNKW: "Unknown",
    WALK: "Walk/PT",
    WALO: "Walk"
};

interface IProps {
    direction: string; 
    mode: string;      
}

function getBandValue(e: any, direction: string) {
    const isOcc = e.ModeCode === 'CARP' || e.ModeCode === 'TAXO';
    const inField = isOcc ? 'OccIn' : 'JnysIn';
    const outField = isOcc ? 'OccOut' : 'JnysOut';
    if (direction === 'Arrivals') return parseInt(e[inField] || "0", 10);
    if (direction === 'Departures') return parseInt(e[outField] || "0", 10);
    return (parseInt(e[inField] || "0", 10) + parseInt(e[outField] || "0", 10));
}

function toHourBand(band) {
    const match = band.match(/^(\d{1,2}):\d{2}-(\d{1,2}):\d{2}$/);
    if (!match) return band;
    const startHour = match[1].padStart(2, '0');
    const endHour = (parseInt(startHour) + 1).toString().padStart(2, '0');
    return `${startHour}00-${endHour}00`;
}


export default function ModeTravlSurveys({ direction, mode }: IProps) {
    const { surveyData } = useTravlData();

    // Find ModeCode(s) matching the long string (should only be one)
    const matchingCodes = Object.keys(modeCodeMap).filter(
        key => modeCodeMap[key] === mode
    );

    // Group by SurveyCode and SurveyDate
    const surveys: Record<string, { date: string, times: Record<string, number> }> = {};
    surveyData
        .filter(e => matchingCodes.includes(e.ModeCode))
        .forEach(e => {
            const code = e.SurveyCode;
            const date = e['SurveyDate'] || '';
            if (!surveys[code]) surveys[code] = { date, times: {} };
            const band = toHourBand(e.TimeBand);   
            const value = getBandValue(e, direction);
            surveys[code].times[band] = (surveys[code].times[band] || 0) + value;
        });


    return (
        <TableContainer component={Paper}>
            <Table size='small' sx={{
                'td, th': {
                    padding: '4px',
                    fontSize: '0.73rem',
                    whiteSpace: 'nowrap',
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }
            }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Survey Code</TableCell>
                        <TableCell>Survey Date</TableCell>
                        {TIME_BANDS.map(band => (
                            <TableCell key={band} align="right">{band}</TableCell>
                        ))}
                        <TableCell align="right">Total</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(surveys).map(([code, { date, times }]) => {
                        const total = TIME_BANDS.reduce((sum, band) => sum + (times[band] || 0), 0);
                        return (
                            <TableRow key={code}>
                                <TableCell>{code}</TableCell>
                                <TableCell>{date}</TableCell>
                                {TIME_BANDS.map(band => (
                                    <TableCell key={band} align="right">
                                        {times[band] ? times[band] : ''}
                                    </TableCell>
                                ))}
                                <TableCell align="right">{total}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
